import React, { useState } from 'react';
import { X, Building2, Save, AlertCircle } from 'lucide-react';

const TenantForm = ({ tenant, onSubmit, onClose }) => {
  const isEditing = !!tenant;
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    slug: tenant?.slug || '',
    billing_email: tenant?.billing_email || '',
    status: tenant?.status || 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      // Auto-generate slug if creating new tenant
      slug: !isEditing && !prev.slug ? generateSlug(name) : prev.slug
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate slug format
      if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        throw new Error('Slug must contain only lowercase letters, numbers, and hyphens');
      }

      const submitData = isEditing
        ? { name: formData.name, billing_email: formData.billing_email, status: formData.status }
        : formData;

      await onSubmit(isEditing ? tenant.id : null, submitData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tenant-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Building2 size={24} color="#667eea" />
            <h2>{isEditing ? 'Edit Tenant' : 'Create Tenant'}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="tenant-form">
          {error && (
            <div className="form-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Tenant Name *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleNameChange}
              placeholder="Acme Corporation"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug *</label>
            <input
              type="text"
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
              placeholder="acme-corp"
              required
              disabled={loading || isEditing}
              pattern="[a-z0-9-]+"
              title="Lowercase letters, numbers, and hyphens only"
            />
            <span className="form-hint">Used in URLs and API identifiers. Cannot be changed after creation.</span>
          </div>

          <div className="form-group">
            <label htmlFor="billing_email">Billing Email</label>
            <input
              type="email"
              id="billing_email"
              value={formData.billing_email}
              onChange={(e) => setFormData(prev => ({ ...prev, billing_email: e.target.value }))}
              placeholder="billing@acme.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Saving...' : (isEditing ? 'Update Tenant' : 'Create Tenant')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantForm;
